import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Fab, Tooltip, Zoom, Drawer, Typography, Divider,
  IconButton, Button, useTheme, Chip, Slider, Switch, FormControlLabel
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SettingsIcon       from '@mui/icons-material/Settings';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DarkModeIcon       from '@mui/icons-material/DarkMode';
import LightModeIcon      from '@mui/icons-material/LightMode';
import CheckIcon          from '@mui/icons-material/Check';
import RefreshIcon        from '@mui/icons-material/Refresh';
import { useThemeMode }   from '@/store/ThemeContext';
import { colorPresets }   from '@/theme';
import type { ThemePreset, ThemeMode } from '@/types';

// ── FAB dragging constants ─────────────────────────────────────────────────
const STORAGE_KEY  = 'lm_fab_pos';
const FAB_SIZE     = 52;
const EDGE_PAD     = 20;
const DRAG_THRESH  = 5;

function loadPos(): { x: number; y: number } | null {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null'); }
  catch { return null; }
}
function clamp(x: number, y: number) {
  return {
    x: Math.max(EDGE_PAD, Math.min(x, window.innerWidth  - FAB_SIZE - EDGE_PAD)),
    y: Math.max(EDGE_PAD, Math.min(y, window.innerHeight - FAB_SIZE - EDGE_PAD)),
  };
}
function snapToEdge(x: number, y: number) {
  const snappedX = x + FAB_SIZE / 2 < window.innerWidth / 2
    ? EDGE_PAD : window.innerWidth - FAB_SIZE - EDGE_PAD;
  return clamp(snappedX, y);
}

// ── Preset swatch colours ──────────────────────────────────────────────────
const PRESET_META: { id: ThemePreset; label: string; from: string; to: string; animated?: boolean }[] = [
  { id: 'sunset',   label: 'Sunset Bloom',     from: '#FF6B6B', to: '#FFD93D' },
  { id: 'ocean',    label: 'Ocean Calm',        from: '#5B8DEF', to: '#14B8A6' },
  { id: 'forest',   label: 'Forest Glow',       from: '#22C55E', to: '#0EA5E9' },
  { id: 'rose',     label: 'Rose Petal',        from: '#F43F5E', to: '#A855F7' },
  { id: 'midnight', label: 'Midnight Blue',     from: '#6366F1', to: '#06B6D4' },
  { id: 'aurora',   label: 'Aurora Borealis',   from: '#8B5CF6', to: '#34D399' },
  { id: 'candy',    label: 'Candy Pop',         from: '#EC4899', to: '#FBBF24' },
  { id: 'ember',    label: 'Ember Charcoal',    from: '#F26522', to: '#212529' },
  { id: 'custom',   label: 'Custom Colors',     from: '#888888', to: '#444444' },
];

// ── Sidebar gradient presets ────────────────────────────────────────────────
const SIDEBAR_PRESET_META: { id: string; label: string; gradient: string; from: string; to: string; animated?: boolean }[] = [
  { id: 'sb-night',    label: 'Night Sky',      gradient: 'linear-gradient(180deg,#0F0C29 0%,#302B63 100%)', from: '#0F0C29', to: '#302B63' },
  { id: 'sb-ocean',    label: 'Deep Ocean',     gradient: 'linear-gradient(180deg,#003973 0%,#006994 100%)', from: '#003973', to: '#006994' },
  { id: 'sb-forest',   label: 'Dark Forest',    gradient: 'linear-gradient(180deg,#0B2516 0%,#1A4731 100%)', from: '#0B2516', to: '#1A4731' },
  { id: 'sb-sunset',   label: 'Warm Ember',     gradient: 'linear-gradient(180deg,#3B0000 0%,#8B2500 100%)', from: '#3B0000', to: '#8B2500' },
  { id: 'sb-purple',   label: 'Royal Purple',   gradient: 'linear-gradient(180deg,#1A003A 0%,#4A1070 100%)', from: '#1A003A', to: '#4A1070' },
  { id: 'sb-slate',    label: 'Slate Carbon',   gradient: 'linear-gradient(180deg,#0F172A 0%,#1E293B 100%)', from: '#0F172A', to: '#1E293B' },
  { id: 'sb-rose',     label: 'Cherry Night',   gradient: 'linear-gradient(180deg,#3B0018 0%,#7F1D3C 100%)', from: '#3B0018', to: '#7F1D3C' },
  // ── Animated presets ──────────────────────────────────────────────────────
  { id: 'sb-bubbles',  label: '✦ Bubbles',       gradient: 'anim:bubbles',      from: '#020B18', to: '#1a2a4a', animated: true },
];



// ── Section label ──────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      letterSpacing={1.3}
      textTransform="uppercase"
      color="text.disabled"
      sx={{ px: 2.5, pt: 2.5, pb: 1, display: 'block' }}
    >
      {children}
    </Typography>
  );
}

// ── Theme Customizer Panel ─────────────────────────────────────────────────
interface ThemeCustomizerPanelProps {
  open: boolean;
  onClose: () => void;
  /** pending local state (not yet saved) */
  pendingMode:   ThemeMode;
  pendingPreset: ThemePreset;
  pendingSidebarColor?: string;
  pendingCustomColors: { primaryHue: number; secondaryHue: number; useGradient: boolean };
  pendingFormCardLightMode: boolean;
  onModeChange:   (m: ThemeMode)   => void;
  onPresetChange: (p: ThemePreset) => void;
  onSidebarColorChange: (c: string | undefined) => void;
  onCustomColorsChange: (colors: { primaryHue: number; secondaryHue: number; useGradient: boolean }) => void;
  onFormCardLightModeChange: (val: boolean) => void;
  onApply:   () => void;
  onDiscard: () => void;
}

function ThemeCustomizerPanel({
  open, onClose,
  pendingMode, pendingPreset, pendingSidebarColor, pendingCustomColors, pendingFormCardLightMode,
  onModeChange, onPresetChange, onSidebarColorChange, onCustomColorsChange, onFormCardLightModeChange,
  onApply, onDiscard,
}: ThemeCustomizerPanelProps) {
  const theme  = useTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      sx={{
        zIndex: 1301,
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          bgcolor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', sm: 400 },
          height: '100%',
          backgroundImage: 'none',
          backgroundColor: alpha(
            theme.palette.background.default,
            theme.palette.mode === 'dark' ? 0.97 : 0.98,
          ),
          backdropFilter: 'blur(20px)',
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: alpha(theme.palette.background.default, 0.88),
          backdropFilter: 'blur(12px)',
        }}
      >
        <IconButton size="small" onClick={onClose} sx={{ mr: 0.5 }}>
          <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography variant="h6" fontWeight={700} flex={1}>
          Theme Customizer
        </Typography>
        <Tooltip title="Reset to default">
          <IconButton
            size="small"
            onClick={() => { 
              onModeChange('light'); 
              onPresetChange('sunset'); 
              onSidebarColorChange(undefined);
            }}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              px: 1.25, py: 0.5, gap: 0.5, fontSize: '0.7rem',
              color: 'primary.main',
            }}
          >
            <RefreshIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={700} sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Reset All
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>

        {/* ─ NAVIGATION BAR ─────────────────────────────────────────────── */}
        <SectionLabel>Navigation Bar</SectionLabel>
        <Box sx={{ px: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Color Mode */}
          <Box
            sx={{
              p: 2, borderRadius: 3,
              bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.4 : 0.7),
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Typography variant="body2" fontWeight={600}>Color Mode</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Switch between light and dark
            </Typography>
            <Box sx={{ display: 'flex', borderRadius: 2.5, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
              {(['light', 'dark'] as ThemeMode[]).map((m) => (
                <Box
                  key={m}
                  onClick={() => onModeChange(m)}
                  sx={{
                    flex: 1, py: 1, textAlign: 'center', cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: 700, textTransform: 'capitalize',
                    transition: 'all 0.2s ease',
                    bgcolor: pendingMode === m
                      ? theme.palette.primary.main
                      : alpha(theme.palette.background.paper, 0.5),
                    color: pendingMode === m ? '#fff' : 'text.secondary',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                  }}
                >
                  {m === 'light' ? <LightModeIcon sx={{ fontSize: 15 }} /> : <DarkModeIcon sx={{ fontSize: 15 }} />}
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </Box>
              ))}
            </Box>
          </Box>

        </Box>

        <Divider sx={{ my: 1.5, mx: 2.5, borderColor: alpha(theme.palette.divider, 0.5) }} />

        {/* ─ FORMS ─────────────────────────────────────────────── */}
        <SectionLabel>Forms</SectionLabel>
        <Box sx={{ px: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              p: 2, borderRadius: 3,
              bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.4 : 0.7),
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>Light Mode Forms</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Force forms to stay light
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={pendingFormCardLightMode}
              onChange={(e) => onFormCardLightModeChange(e.target.checked)}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5, mx: 2.5, borderColor: alpha(theme.palette.divider, 0.5) }} />

        {/* ─ SIDEBAR PRESETS ───────────────────────────────────────────── */}
        <SectionLabel>Sidebar Presets</SectionLabel>
        <Box sx={{ px: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
            {SIDEBAR_PRESET_META.map((p) => {
              const isActive = pendingSidebarColor === p.gradient;
              return (
                <Box
                  key={p.id}
                  onClick={() => onSidebarColorChange(isActive ? undefined : p.gradient)}
                  sx={{
                    flexShrink: 0,
                    width: 100,
                    cursor: 'pointer',
                    borderRadius: 3.5,
                    overflow: 'hidden',
                    border: `2px solid ${isActive ? alpha('#fff', 0.7) : alpha(theme.palette.divider, 0.4)}`,
                    boxShadow: isActive ? `0 6px 22px ${alpha(p.from, 0.55)}` : 'none',
                    transition: 'all 0.22s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(p.from, 0.38)}`,
                    },
                  }}
                >
                  {/* gradient / animated swatch */}
                  <Box
                    sx={{
                      height: 70,
                      background: p.animated
                        ? `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)`
                        : p.gradient,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Animated shimmer overlay for anim presets */}
                    {p.animated && (
                      <Box sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.15) 50%,transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'sbAuroraShift 2s linear infinite',
                      }} />
                    )}

                    {isActive ? (
                      <Chip
                        label="ACTIVE"
                        size="small"
                        sx={{
                          bgcolor: alpha('#fff', 0.22), color: '#fff',
                          fontSize: '0.58rem', fontWeight: 800, height: 18, letterSpacing: 0.8,
                          zIndex: 1,
                        }}
                      />
                    ) : p.animated ? (
                      <Chip
                        label="LIVE"
                        size="small"
                        sx={{
                          bgcolor: alpha('#fff', 0.18), color: '#fff',
                          fontSize: '0.55rem', fontWeight: 800, height: 16, letterSpacing: 0.6,
                          zIndex: 1,
                        }}
                      />
                    ) : null}

                    {/* decorative circle */}
                    <Box sx={{
                      position: 'absolute', bottom: -16, right: -10,
                      width: 48, height: 48, borderRadius: '50%',
                      bgcolor: alpha('#fff', 0.1), pointerEvents: 'none',
                    }} />
                  </Box>
                  <Box sx={{ px: 1.25, py: 0.875, bgcolor: alpha(theme.palette.background.paper, 0.85) }}>
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', fontSize: '0.7rem' }}>
                      {p.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* reset */}
          {pendingSidebarColor && (
            <Button
              variant="text"
              size="small"
              onClick={() => onSidebarColorChange(undefined)}
              sx={{ mt: 1, px: 0, color: 'text.disabled', fontSize: '0.7rem', textTransform: 'none' }}
            >
              ✕ Reset sidebar to default
            </Button>
          )}
        </Box>

        <Divider sx={{ my: 1.5, mx: 2.5, borderColor: alpha(theme.palette.divider, 0.5) }} />
        {/* ─ DASHBOARD PRESETS ──────────────────────────────────────────── */}
        <SectionLabel>Dashboard Presets</SectionLabel>
        <Box sx={{ px: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
            {PRESET_META.map((p) => {
              const active = pendingPreset === p.id;
              return (
                <Box
                  key={p.id}
                  onClick={() => onPresetChange(p.id)}
                  sx={{
                    flexShrink: 0,
                    width: 110,
                    cursor: 'pointer',
                    borderRadius: 3.5,
                    overflow: 'hidden',
                    border: `2px solid ${active ? colorPresets[p.id].primary.main : alpha(theme.palette.divider, 0.4)}`,
                    boxShadow: active ? `0 6px 22px ${alpha(colorPresets[p.id].primary.main, 0.36)}` : 'none',
                    transition: 'all 0.22s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(colorPresets[p.id].primary.main, 0.28)}` },
                  }}
                >
                  {/* gradient swatch */}
                  <Box
                    sx={{
                      height: 74,
                      background: p.id === 'custom'
                        ? (pendingCustomColors.useGradient
                           ? `linear-gradient(135deg, hsl(${pendingCustomColors.primaryHue}, 78%, 56%) 0%, hsl(${pendingCustomColors.secondaryHue}, 70%, 54%) 100%)`
                           : `hsl(${pendingCustomColors.primaryHue}, 78%, 56%)`)
                        : `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)`,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {active ? (
                      <Chip
                        label="ACTIVE"
                        size="small"
                        sx={{ bgcolor: alpha('#fff', 0.22), color: '#fff', fontSize: '0.6rem', fontWeight: 800, height: 20, letterSpacing: 1, zIndex: 1 }}
                      />
                    ) : p.animated ? (
                      <Chip
                        label="LIVE"
                        size="small"
                        sx={{
                          bgcolor: alpha('#fff', 0.18), color: '#fff',
                          fontSize: '0.55rem', fontWeight: 800, height: 16, letterSpacing: 0.6,
                          zIndex: 1,
                        }}
                      />
                    ) : null}
                    {/* decorative circle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -18,
                        right: -10,
                        width: 54,
                        height: 54,
                        borderRadius: '50%',
                        bgcolor: alpha('#fff', 0.14),
                      }}
                    />
                  </Box>
                  <Box sx={{ px: 1.25, py: 1, bgcolor: alpha(theme.palette.background.paper, 0.85) }}>
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', fontSize: '0.75rem' }}>
                      {p.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Custom color pickers if custom is selected */}
          {pendingPreset === 'custom' && (
            <Box sx={{ mt: 3, p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.background.paper, 0.4), border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" fontWeight={700}>Edit Theme Colors</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={pendingCustomColors.useGradient}
                      onChange={(e) => onCustomColorsChange({ ...pendingCustomColors, useGradient: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption" color="text.secondary">Two Colors</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0 }}>
                    {pendingCustomColors.useGradient ? 'Color 1 (Primary)' : 'Solid Color'}
                  </Typography>
                  <Slider
                    min={0} max={360}
                    value={pendingCustomColors.primaryHue}
                    onChange={(_, val) => onCustomColorsChange({ ...pendingCustomColors, primaryHue: val as number })}
                    sx={{
                      color: `hsl(${pendingCustomColors.primaryHue}, 78%, 56%)`,
                      '& .MuiSlider-thumb': { border: '2px solid #fff' }
                    }}
                  />
                </Box>
                {pendingCustomColors.useGradient && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0 }}>Color 2 (Secondary)</Typography>
                    <Slider
                      min={0} max={360}
                      value={pendingCustomColors.secondaryHue}
                      onChange={(_, val) => onCustomColorsChange({ ...pendingCustomColors, secondaryHue: val as number })}
                      sx={{
                        color: `hsl(${pendingCustomColors.secondaryHue}, 70%, 54%)`,
                        '& .MuiSlider-thumb': { border: '2px solid #fff' }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

        </Box>
      </Box>


      {/* ── Sticky footer ───────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5, py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          display: 'flex', gap: 1.5,
          bgcolor: alpha(theme.palette.background.default, 0.9),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Button variant="outlined" fullWidth onClick={onDiscard} sx={{ borderRadius: 3 }}>
          Discard
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onApply}
          startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: 3, fontWeight: 700,
            background: theme.custom.gradients.primary,
            color: '#fff',
            boxShadow: `0 8px 24px ${alpha(colorPresets[pendingPreset].primary.main, 0.36)}`,
          }}
        >
          Save Theme
        </Button>
      </Box>
      </Box>
    </Drawer>
  );
}

// ── Main FloatingButton export ─────────────────────────────────────────────
export function FloatingButton() {
  const theme = useTheme();
  const { mode, preset, sidebarColor, customColors, formCardLightMode, setMode, setPreset, setSidebarColor, setCustomColors, setFormCardLightMode } = useThemeMode();

  // FAB position
  const saved = loadPos();
  const [pos, setPos] = useState(() => {
    const p = saved ?? { x: window.innerWidth - FAB_SIZE - EDGE_PAD, y: Math.round(window.innerHeight * 0.62) };
    return clamp(p.x, p.y);
  });
  const [dragging,    setDragging]    = useState(false);
  const [panelOpen,   setPanelOpen]   = useState(false);
  const [spin,        setSpin]        = useState(false);

  // Pending (unsaved) customizer state
  const [pendingMode,   setPendingMode]   = useState<ThemeMode>(mode);
  const [pendingPreset, setPendingPreset] = useState<ThemePreset>(preset);
  const [pendingSidebarColor, setPendingSidebarColor] = useState<string | undefined>(sidebarColor);
  const [pendingCustomColors, setPendingCustomColors] = useState<{ primaryHue: number; secondaryHue: number; useGradient: boolean }>(customColors || { primaryHue: 0, secondaryHue: 180, useGradient: true });
  const [pendingFormCardLightMode, setPendingFormCardLightMode] = useState<boolean>(formCardLightMode);

  // Sync pending when real theme changes externally
  useEffect(() => { setPendingMode(mode);     }, [mode]);
  useEffect(() => { setPendingPreset(preset); }, [preset]);
  useEffect(() => { setPendingSidebarColor(sidebarColor); }, [sidebarColor]);
  useEffect(() => { setPendingCustomColors(customColors || { primaryHue: 0, secondaryHue: 180, useGradient: true }); }, [customColors]);
  useEffect(() => { setPendingFormCardLightMode(formCardLightMode); }, [formCardLightMode]);

  const isDragging = useRef(false);
  const hasMoved   = useRef(false);
  const start      = useRef({ mx: 0, my: 0, bx: 0, by: 0 });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  }, [pos]);

  // ── pointer ──────────────────────────────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    isDragging.current = true;
    hasMoved.current   = false;
    start.current = { mx: e.clientX, my: e.clientY, bx: pos.x, by: pos.y };
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - start.current.mx;
    const dy = e.clientY - start.current.my;
    if (Math.abs(dx) > DRAG_THRESH || Math.abs(dy) > DRAG_THRESH) hasMoved.current = true;
    setPos(clamp(start.current.bx + dx, start.current.by + dy));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setDragging(false);
    setPos((prev) => {
      const snapped = snapToEdge(prev.x, prev.y);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped));
      return snapped;
    });
    if (!hasMoved.current) {
      setSpin(true);
      setTimeout(() => setSpin(false), 450);
      setPanelOpen((p) => !p);
    }
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  // ── apply / discard ───────────────────────────────────────────────────────
  const handleApply = () => {
    setMode(pendingMode);
    setPreset(pendingPreset);
    setSidebarColor(pendingSidebarColor || '');
    setCustomColors(pendingCustomColors);
    setFormCardLightMode(pendingFormCardLightMode);
    setPanelOpen(false);
  };

  const handleDiscard = () => {
    setPendingMode(mode);
    setPendingPreset(preset);
    setPendingSidebarColor(sidebarColor);
    setPendingCustomColors(customColors || { primaryHue: 0, secondaryHue: 180, useGradient: true });
    setPendingFormCardLightMode(formCardLightMode);
    setPanelOpen(false);
  };

  const isRight = pos.x + FAB_SIZE / 2 > window.innerWidth / 2;

  return (
    <>
      {/* ── FAB ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          position:    'fixed',
          left:        pos.x,
          top:         pos.y,
          zIndex:      (t) => t.zIndex.modal + 20,
          userSelect:  'none',
          touchAction: 'none',
          // Hide FAB while panel is open
          opacity:       panelOpen ? 0 : 1,
          pointerEvents: panelOpen ? 'none' : 'auto',
          transform:     panelOpen ? 'scale(0.6)' : 'scale(1)',
          transition:  dragging
            ? 'none'
            : 'left 0.28s cubic-bezier(0.34,1.56,0.64,1), top 0.18s ease, opacity 0.2s ease, transform 0.2s ease',
        }}
      >
        <Tooltip
          title={panelOpen ? '' : 'Theme Customizer'}
          placement={isRight ? 'left' : 'right'}
          TransitionComponent={Zoom}
          arrow
        >
          <Fab
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            size="medium"
            disableRipple={dragging}
            sx={{
              width: FAB_SIZE,
              height: FAB_SIZE,
              minHeight: 'unset',
              background: panelOpen
                ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12)
                : theme.custom.gradients.primary,
              color: panelOpen ? 'primary.main' : '#fff',
              boxShadow: panelOpen
                ? `0 0 0 2.5px ${alpha(theme.palette.primary.main, 0.45)}, 0 6px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                : `0 8px 28px ${alpha(theme.palette.primary.main, 0.46)}`,
              cursor: dragging ? 'grabbing' : 'grab',
              transition: 'background 0.22s ease, box-shadow 0.22s ease, transform 0.15s ease',
              transform: dragging ? 'scale(1.1)' : 'scale(1)',
              '&:hover': {
                transform: dragging ? 'scale(1.1)' : 'scale(1.07)',
                boxShadow: `0 14px 36px ${alpha(theme.palette.primary.main, 0.54)}`,
              },
              '&:active': { transform: 'scale(0.94)' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                animation: 'spin-gear 8s linear infinite',
                '@keyframes spin-gear': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            >
              <SettingsIcon
                sx={{
                  fontSize: 22,
                  transition: 'transform 0.42s cubic-bezier(0.34,1.56,0.64,1)',
                  transform: spin ? 'rotate(180deg)' : panelOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </Fab>
        </Tooltip>

        {/* drag-handle dots */}
        <Box
          sx={{
            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
            ...(isRight ? { right: FAB_SIZE + 4 } : { left: FAB_SIZE + 4 }),
            display: 'flex', flexDirection: 'column', gap: '4px',
            opacity: dragging ? 1 : 0, transition: 'opacity 0.18s', pointerEvents: 'none',
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: alpha(theme.palette.text.primary, 0.45) }} />
          ))}
        </Box>
      </Box>

      {/* ── Theme Customizer Panel ───────────────────────────────────────── */}
        <ThemeCustomizerPanel
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          pendingMode={pendingMode}
          pendingPreset={pendingPreset}
          pendingSidebarColor={pendingSidebarColor}
          pendingCustomColors={pendingCustomColors}
          pendingFormCardLightMode={pendingFormCardLightMode}
          onModeChange={setPendingMode}
          onPresetChange={setPendingPreset}
          onSidebarColorChange={setPendingSidebarColor}
          onCustomColorsChange={setPendingCustomColors}
          onFormCardLightModeChange={setPendingFormCardLightMode}
          onApply={handleApply}
          onDiscard={handleDiscard}
        />
    </>
  );
}
